/**
 * Players-View: Hero + Stat-Karten (Spieler/Map/Modus/Tickets) + zweispaltiges
 * Scoreboard nach Team (PAC links, EU rechts; mobil gestapelt) + TAC-Platzhalter.
 * Live über die WS-Signals (players/status); initial + manueller Refresh über
 * REST. Tickets-Steuerung und FBF liegen jetzt auf der Maps-View.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { useRef } from "preact/hooks";
import { createPortal } from "preact/compat";
import { api, live, activeServer, servers, isAdmin, hasPermission } from "../store";
import { t } from "../i18n";
import { Stat, Panel } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { ServerKillfeedPanel } from "./killfeed-panel";
import { parseBf2ccPl, fromLivePlayers, mergeScorePlayers, modeLabel, formatBattleResource, formatMapName, rankIconPath, playerTacTelemetry, type BattleResource, type PlayerTacTelemetry, type ScorePlayer } from "../parse";
import type { ApiResponse } from "../api";
import { toast } from "../toast";
import { resolveTacCalibration, resolveTacCrop } from "../../tac/legacy-calibrations";
import { tacPointImageKey, visibleTacPoints, pointFromClientRect, type TacPoint } from "../../tac/points";
import {
  viewportToImagePoint,
  cropFromDrag,
  fitViewportFromImagePoints,
  type TacViewportRect,
  type TacCalibCapture,
} from "../../tac/calibrate";
import {
  staticObjectsForMap,
  pickStaticObjectVariant,
  staticObjectsToTacPoints,
  mergeTacPoints,
  parseStaticObjects,
  type StaticObjectData,
} from "../../tac/static-objects";
import { curatedTitanMap, curatedToTacPoints } from "../../tac/titan-objects";
import { resolveVehicleDisplayName } from "../../game/bf2142-display-names";
import { usePolling } from "../use-polling";

function RankCell({ rank }: { rank: string }) {
  const src = rankIconPath(rank);
  return (
    <td class="rank-cell" data-label={t("field.rank")}>
      {src ? (
        <img class="rank-icon" src={src} alt={`${t("field.rank")} ${rank}`} title={`${t("field.rank")} ${rank}`} />
      ) : (
        <span class="mono faint">{rank || "—"}</span>
      )}
    </td>
  );
}

export function teamTotals(players: ScorePlayer[]): { score: number; kills: number; deaths: number } {
  return players.reduce(
    (acc, p) => ({
      score: acc.score + p.score,
      kills: acc.kills + p.kills,
      deaths: acc.deaths + p.deaths,
    }),
    { score: 0, kills: 0, deaths: 0 },
  );
}

const TAC_MAPS = [
  "Belgrade",
  "Bridge_at_Remagen",
  "Camp_Gibraltar",
  "Cerbere_Landing",
  "Fall_of_Berlin",
  "Highway_Tampa",
  "Liberation_of_Leipzig",
  "Minsk",
  "Molokai",
  "Operation_Blue_Pearl",
  "Operation_Shingle",
  "Port_Bavaria",
  "Shuhia_Taiba",
  "Sidi_Power_Plant",
  "Strike_at_Karkand",
  "Tunis_Harbor",
  "Verdun",
  "Wake_Island_2142",
  "Yellow_Knife",
  "suez_canal",
] as const;

export interface TacMapAsset {
  label: string;
  slug: string;
  src: string | null;
  known: boolean;
}

export function normalizeTacMapKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function formatTacMapLabel(value: string): string {
  return formatMapName(value);
}

export function resolveTacMapAsset(mapName: string): TacMapAsset {
  const wanted = normalizeTacMapKey(mapName);
  const match = TAC_MAPS.find((m) => {
    const key = normalizeTacMapKey(m);
    return key === wanted || (!!wanted && (key.includes(wanted) || wanted.includes(key)));
  });
  const label = formatTacMapLabel(mapName || match || "");
  if (!match) return { label, slug: wanted || "unknown", src: null, known: false };
  return {
    label: formatTacMapLabel(match),
    slug: normalizeTacMapKey(match),
    src: `/assets/maps/${match}/minimap.webp`,
    known: true,
  };
}

export function tacSectorLabels(): string[] {
  return ["A1", "A2", "A3", "B1", "B2", "B3", "C1", "C2", "C3"];
}

export function tacTelemetryStatus(): string {
  return "POSITION TELEMETRY: OFFLINE";
}

export type TacTelemetryState = "offline" | "no-position" | "needs-calibration" | "live";

export function tacTelemetryStatusFor(state: TacTelemetryState): string {
  switch (state) {
    case "live":
      return "POSITION TELEMETRY: LIVE";
    case "needs-calibration":
      return "POSITION TELEMETRY: NEEDS CALIBRATION";
    case "no-position":
      return "POSITION TELEMETRY: NO POSITION DATA";
    default:
      return "POSITION TELEMETRY: OFFLINE";
  }
}

export function showTacCenterInfo(state: TacTelemetryState): boolean {
  return state !== "live";
}

export function tacMapStateClass(state: TacTelemetryState): string {
  return state === "live" ? "live" : "";
}

type TacCalibPoint = { p: [number, number]; w: [number, number] };
export interface TacImageViewport {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}

const TAC_LEGACY_VIEW_ASPECT = 5 / 3;

function validCalibPoint(value: unknown): TacCalibPoint | null {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : null;
  const p = Array.isArray(raw?.p) ? raw.p.map(Number) : [];
  const w = Array.isArray(raw?.w) ? raw.w.map(Number) : [];
  if (p.length < 2 || w.length < 2) return null;
  if (!p.every(Number.isFinite) || !w.every(Number.isFinite)) return null;
  // The current CSS overlay can place normalized image coordinates only.
  if (p[0]! < 0 || p[0]! > 1 || p[1]! < 0 || p[1]! > 1) return null;
  return { p: [p[0]!, p[1]!], w: [w[0]!, w[1]!] };
}

function normalizedCalib(calib: unknown): TacCalibPoint[] {
  if (!Array.isArray(calib)) return [];
  return calib.map(validCalibPoint).filter((p): p is TacCalibPoint => p !== null).slice(0, 3);
}

function det3(m: number[][]): number {
  return m[0]![0]! * (m[1]![1]! * m[2]![2]! - m[1]![2]! * m[2]![1]!)
    - m[0]![1]! * (m[1]![0]! * m[2]![2]! - m[1]![2]! * m[2]![0]!)
    + m[0]![2]! * (m[1]![0]! * m[2]![1]! - m[1]![1]! * m[2]![0]!);
}

function solveAxis(points: TacCalibPoint[], axis: 0 | 1): [number, number, number] | null {
  const m = points.map((pt) => [pt.w[0], pt.w[1], 1]);
  const d = det3(m);
  if (Math.abs(d) < 1e-9) return null;
  const y = points.map((pt) => pt.p[axis]);
  const replace = (col: number) => m.map((row, i) => row.map((v, j) => (j === col ? y[i]! : v)));
  return [det3(replace(0)) / d, det3(replace(1)) / d, det3(replace(2)) / d];
}

export function mapWorldToTacPoint(calib: unknown, world: { x: number; z: number }): { x: number; y: number } | null {
  const uv = mapWorldToTacImagePoint(calib, world);
  if (!uv) return null;
  return mapImagePointToViewport(uv, legacyTacViewport());
}

export function mapWorldToTacImagePoint(calib: unknown, world: { x: number; z: number }): { x: number; y: number } | null {
  const points = normalizedCalib(calib);
  if (points.length !== 3) return null;
  const ux = solveAxis(points, 0);
  const vy = solveAxis(points, 1);
  if (!ux || !vy) return null;
  const x = ux[0] * world.x + ux[1] * world.z + ux[2];
  const y = vy[0] * world.x + vy[1] * world.z + vy[2];
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
}

export function coverImageViewport(imageAspect = 1, containerAspect = TAC_LEGACY_VIEW_ASPECT): TacImageViewport {
  const imgA = Number.isFinite(imageAspect) && imageAspect > 0 ? imageAspect : 1;
  const boxA = Number.isFinite(containerAspect) && containerAspect > 0 ? containerAspect : TAC_LEGACY_VIEW_ASPECT;
  let u0 = 0;
  let u1 = 1;
  let v0 = 0;
  let v1 = 1;
  if (imgA < boxA) {
    const visibleH = imgA / boxA;
    v0 = (1 - visibleH) / 2;
    v1 = v0 + visibleH;
  } else if (imgA > boxA) {
    const visibleW = boxA / imgA;
    u0 = (1 - visibleW) / 2;
    u1 = u0 + visibleW;
  }
  return { u0, u1, v0, v1 };
}

export function legacyTacViewport(aspect = TAC_LEGACY_VIEW_ASPECT): TacImageViewport {
  return coverImageViewport(1, aspect);
}

/**
 * Container-Aspect aus gemessenen Pixeln, auf 1/500 quantisiert. Pure + testbar.
 * Zweck: ResizeObserver liefert Sub-Pixel-Rauschen — ohne Quantisierung erzeugt
 * jede Mess-Schwankung einen neuen Aspect-Wert → Re-Render → potenziell erneut
 * ein RO-Tick (Jitter-Schleife). Ungültige Maße → null (Messung verwerfen).
 */
export function quantizedAspect(w: number, h: number): number | null {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return Math.round((w / h) * 500) / 500;
}

export function mapImagePointToViewport(point: { x: number; y: number }, viewport: TacImageViewport): { x: number; y: number } | null {
  const w = viewport.u1 - viewport.u0;
  const h = viewport.v1 - viewport.v0;
  if (w <= 0 || h <= 0) return null;
  const x = (point.x - viewport.u0) / w;
  const y = (point.y - viewport.v0) / h;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  // Außerhalb des sichtbaren Ausschnitts -> nicht rendern (legacy-nah: geclippt).
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
}

/** Validiert/normalisiert einen per-Map-Crop `{u0,u1,v0,v1}` (0..1, u0<u1, v0<v1). */
export function normalizeTacCrop(crop: unknown): TacImageViewport | null {
  const c = crop && typeof crop === "object" ? crop as Record<string, unknown> : null;
  if (!c) return null;
  const u0 = Number(c.u0), u1 = Number(c.u1), v0 = Number(c.v0), v1 = Number(c.v1);
  if (![u0, u1, v0, v1].every(Number.isFinite)) return null;
  const cu0 = Math.max(0, Math.min(1, Math.min(u0, u1)));
  const cu1 = Math.max(0, Math.min(1, Math.max(u0, u1)));
  const cv0 = Math.max(0, Math.min(1, Math.min(v0, v1)));
  const cv1 = Math.max(0, Math.min(1, Math.max(v0, v1)));
  if (cu1 - cu0 < 0.02 || cv1 - cv0 < 0.02) return null; // leerer/winziger Ausschnitt -> ignorieren
  return { u0: cu0, u1: cu1, v0: cv0, v1: cv1 };
}

/**
 * Macht aus einem per-Map-Crop den GETEILTEN Overlay-Viewport: der Crop wird
 * verzerrungsfrei („cover") auf das Container-Seitenverhältnis erweitert und in
 * [0,1] geklemmt. Dieser EINE Viewport speist Bilddarstellung UND Objekt-/
 * Spielerprojektion. Minimaps sind quadratisch (imageAspect=1). Kein Crop -> null
 * (Aufrufer fällt aufs bisherige Cover-Verhalten zurück).
 */
export function tacCropViewport(crop: unknown, imageAspect = 1, containerAspect = TAC_LEGACY_VIEW_ASPECT): TacImageViewport | null {
  const c = normalizeTacCrop(crop);
  if (!c) return null;
  const A = Number.isFinite(containerAspect) && containerAspect > 0 ? containerAspect : TAC_LEGACY_VIEW_ASPECT;
  const W = Number.isFinite(imageAspect) && imageAspect > 0 ? imageAspect : 1;
  const H = 1;
  let x0 = c.u0 * W, y0 = c.v0 * H, w = (c.u1 - c.u0) * W, h = (c.v1 - c.v0) * H;
  if (w / h < A) { const nw = Math.min(h * A, W); x0 -= (nw - w) / 2; w = nw; }
  else if (w / h > A) { const nh = Math.min(w / A, H); y0 -= (nh - h) / 2; h = nh; }
  if (x0 < 0) x0 = 0; if (x0 + w > W) x0 = W - w;
  if (y0 < 0) y0 = 0; if (y0 + h > H) y0 = H - h;
  if (w > W) { w = W; h = W / A; x0 = 0; y0 = Math.max(0, Math.min(H - h, y0)); }
  if (h > H) { h = H; w = H * A; y0 = 0; x0 = Math.max(0, Math.min(W - w, x0)); }
  return { u0: x0 / W, u1: (x0 + w) / W, v0: y0 / H, v1: (y0 + h) / H };
}

/**
 * CSS für die Bilddarstellung eines Crop-Viewports via `background-image`, sodass
 * GENAU der sichtbare Ausschnitt den Container füllt — identisch zur Projektion.
 */
export function tacCropImageStyle(src: string, vp: TacImageViewport): string {
  const cw = vp.u1 - vp.u0, ch = vp.v1 - vp.v0;
  const sizeX = (100 / cw).toFixed(3);
  const sizeY = (100 / ch).toFixed(3);
  const posX = cw >= 1 ? "0" : (100 * vp.u0 / (1 - cw)).toFixed(3);
  const posY = ch >= 1 ? "0" : (100 * vp.v0 / (1 - ch)).toFixed(3);
  return `background-image:url(${src});background-size:${sizeX}% ${sizeY}%;background-position:${posX}% ${posY}%;background-repeat:no-repeat`;
}

export function kitIconKey(kit: string | null | undefined): "assault" | "engineer" | "recon" | "support" | null {
  const k = String(kit ?? "").toLowerCase().replace(/^(pac_|eu_|us_)/, "");
  if (k.includes("assault") || k.includes("medic")) return "assault";
  if (k.includes("engineer") || k.includes("anti-vehicle") || k.includes("anti_vehicle") || k.includes("antivehicle")) return "engineer";
  if (k.includes("recon") || k.includes("sniper") || k.includes("scout")) return "recon";
  if (k.includes("support") || k.includes("ammo") || k.includes("supply")) return "support";
  return null;
}

export function kitIconPath(kit: string | null | undefined): string | null {
  const key = kitIconKey(kit);
  return key ? `/assets/kits/role_${key}.png` : null;
}

export function vehicleName(raw: string | null | undefined): string {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const r = value.toLowerCase();
  if (r === "0" || r === "none" || r.includes("soldier") || r.includes("freecamera") || r.includes("ladder") || r.includes("container")) return "";
  return value.replace(/^(pac_|eu_|us_)/i, "").replace(/_/g, " ");
}

export function vehicleIconKey(raw: string | null | undefined, team: 0 | 1 | 2): string | null {
  const r = String(raw ?? "").toLowerCase();
  if (!vehicleName(r)) return null;
  const fac = team === 1 ? "as" : "eu";
  if (/(droppod|drop_pod|^pod$|_pod)/.test(r)) return "pod";
  if (r.includes("parachute") || r.includes("chute")) return "parachute";
  if (r.includes("titan")) {
    if (r.includes("aa")) return "titan_aa";
    if (r.includes("ag") || r.includes("artillery") || r.includes("gun") || r.includes("cannon")) return "titan_ag";
    return `${fac}_titan`;
  }
  if (r.includes("hover")) return "pac_hover";
  if (/(static|emplace|stationary)/.test(r)) {
    if (r.includes("aa")) return "static_aa";
    if (r.includes("av") || r.includes("tv")) return "static_av";
  }
  if (r.includes("walker") || r.includes("mech")) return `${fac}_walker`;
  if (r.includes("ifv")) return "eu_ifv";
  if (/(_ag|gunship|wraith)/.test(r)) return `${fac}_ag`;
  if (/(_at|transport|valk|chopper)/.test(r)) return `${fac}_at`;
  if (r.includes("apc")) return `${fac}_apc`;
  if (/(fav|buggy|speeder|jeep)/.test(r)) return `${fac}_fav`;
  if (r.includes("tank")) return `${fac}_tank`;
  return null;
}

export function vehicleIconPath(raw: string | null | undefined, team: 0 | 1 | 2): string | null {
  const key = vehicleIconKey(raw, team);
  return key ? `/assets/vehicles/${key}.png` : null;
}

/**
 * Fahrzeug-Rohwert (Feld 38) -> lesbarer BF2142-Anzeigename (Issue #13). Bekannte
 * Engine-Codes wie `us_ag`/`as_ag`/`eu_tank` werden zu den konkreten Fahrzeug-
 * namen ("UD-6 Talon"/"Type 4 Doragon"/"A8 Tiger") statt zu Short-Codes ("AG").
 * Reihenfolge: exakter Key -> normalisierter Key -> Shortcode+Team-Fallback
 * (`resolveVehicleDisplayName`, Quelle der Wahrheit: `VEHICLE_DISPLAY_NAMES`);
 * unbekannte Codes behalten den bisherigen Roh-Fallback (`vehicleName`);
 * soldier/none/leer (und bewusst leere Mapping-Werte) bleiben leer.
 */
export function vehicleDisplayName(raw: string | null | undefined, team: 0 | 1 | 2 = 0): string {
  const resolved = resolveVehicleDisplayName(raw, team);
  if (resolved !== undefined) return resolved;
  return vehicleName(raw);
}

export function tacPlayerMeta(p: PlayerTacTelemetry): string {
  const parts: string[] = [];
  if (p.commander) parts.push("CMD");
  if (p.squadLeader) parts.push("SL");
  if (p.squad && p.squad !== "0") parts.push(`SQ ${p.squad}`);
  // Echtes Kit (nicht "none"/"0") -> anzeigen; sonst kein "none" pushen.
  const kit = String(p.kit ?? "").trim();
  const realKit = kit !== "" && kit.toLowerCase() !== "none" && kit !== "0";
  if (realKit) parts.push(kit.replace(/^(pac_|eu_|us_)/i, "").replace(/_/g, " "));
  const veh = vehicleDisplayName(p.vehicle, p.team);
  if (veh) parts.push(veh);
  // Kein Kit + kein Fahrzeug + kein Commander = tot/unspawned -> "DEAD" statt "NONE".
  if (!realKit && !veh && !p.commander) parts.push("DEAD");
  return parts.join(" · ");
}

export function showTacCalibrationButton(admin: boolean): boolean {
  return admin;
}

export function tacCalibrationTarget(): "maps" {
  return "maps";
}

function playerActionId(id: unknown): number | null {
  const n = Number(id);
  if (!Number.isInteger(n) || n < 0 || String(id).trim() === "") return null;
  return n;
}

export function kickCommandForPlayer(id: unknown): string | null {
  const n = playerActionId(id);
  return n == null ? null : `admin.kickPlayer ${n}`;
}

export function banCommandForPlayer(id: unknown): string | null {
  const n = playerActionId(id);
  return n == null ? null : `admin.banPlayer ${n} perm`;
}

export function canCopyPlayerValue(value: string): boolean {
  const v = value.trim();
  return v !== "" && v !== "—";
}

export async function copyTextToClipboard(value: string): Promise<boolean> {
  const text = value.trim();
  if (!canCopyPlayerValue(text)) return false;

  const g = globalThis as typeof globalThis & {
    navigator?: { clipboard?: { writeText?: (value: string) => Promise<void> } };
    document?: Document;
  };

  try {
    if (g.navigator?.clipboard?.writeText) {
      await g.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // HTTP/LAN-Zugriffe sind oft kein secure context; dann auf execCommand fallen.
  }

  const doc = g.document;
  if (!doc?.body?.appendChild || !doc.createElement || !doc.execCommand) return false;

  const textarea = doc.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  doc.body.appendChild(textarea);
  try {
    textarea.select();
    return doc.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

export function scoreboardColumns(admin: boolean): string[] {
  const cols = ["Rang", "Spieler", "PID", "IP", "Score", "K", "D", "Ping"];
  return admin ? [...cols, "Aktionen"] : cols;
}

/** Wie oft die IP-Spalte (nur mit Permission) über den gated REST-Pfad nachgezogen wird. */
export const IP_BACKFILL_POLL_MS = 10000;

/**
 * Nach einem erfolglosen Backfill-Versuch (IP weiterhin leer) wird derselbe
 * Spieler frühestens nach dieser Zeit erneut versucht. KEIN dauerhafter Lockout:
 * erscheint die IP später (z.B. nach vollständigem Connect), wird sie ohne
 * Page-Reload nachgezogen — aber kein aggressives Sekunden-Polling.
 */
export const IP_BACKFILL_RETRY_MS = 30000;

/**
 * Stabiler Tracking-Key eines Spielers, der einen IP-Nachzug rechtfertigt — oder
 * `null`, wenn die Row keinen Backfill auslösen darf. Ausgeschlossen sind nur:
 * Bots (haben keine IP), Rows die bereits eine IP haben, sowie leere/ungültige
 * Rows (weder pid noch gültige slot-id). pid bevorzugt, sonst slot-id.
 *
 * Commander sind ECHTE Spieler und werden NICHT ausgeschlossen — auch sie müssen
 * für Berechtigte ihre IP per Backfill bekommen.
 */
export function playerBackfillKey(p: ScorePlayer): string | null {
  if (p.bot) return null;
  if (p.ip.trim() !== "") return null;
  if (p.pid && p.pid.trim() !== "") return `pid:${p.pid}`;
  if (Number.isInteger(p.id) && p.id >= 0) return `id:${p.id}`;
  return null;
}

/**
 * Welche Spieler-Keys sollen (permission-gated) per REST nachgezogen werden?
 * Nur echte Spieler ohne IP (siehe `playerBackfillKey`), deren Retry-Cooldown
 * abgelaufen ist (`now >= nextRetryAt` bzw. noch kein Eintrag). Ohne
 * `action.view_ip` IMMER leer → nie eine IP-Anfrage.
 */
export function ipBackfillTargets(
  rows: ScorePlayer[],
  canViewIp: boolean,
  retry: Map<string, number>,
  now: number = Date.now(),
): string[] {
  if (!canViewIp) return [];
  const out: string[] = [];
  for (const p of rows) {
    const key = playerBackfillKey(p);
    if (!key) continue;
    const nextAt = retry.get(key);
    if (nextAt !== undefined && now < nextAt) continue; // noch im Retry-Cooldown
    out.push(key);
  }
  return out;
}

/**
 * Braucht das Scoreboard einen (permission-gated) REST-Nachzug der IP-Spalte?
 *
 * Hintergrund: Der Worker-WS-Push enthält bewusst KEINE IP (siehe
 * `playersForPush` im Worker), damit nie ungeschützt eine IP über den WS leakt.
 * Die IP kommt ausschließlich über den server-seitig gegateten REST-Pfad
 * (`players_list` -> `plStripIp` für Nicht-Berechtigte). Spieler, die NACH dem
 * initialen REST-Load dazukommen, erscheinen nur per WS → ihre IP-Spalte bliebe
 * leer. `true` nur, wenn es fällige Targets gibt (Permission + echte Spieler ohne
 * IP + Retry-Cooldown abgelaufen) → kein aggressives Dauerpolling, aber auch kein
 * dauerhafter Lockout.
 */
export function needsIpBackfill(
  rows: ScorePlayer[],
  canViewIp: boolean,
  retry: Map<string, number> = new Map(),
  now: number = Date.now(),
): boolean {
  return ipBackfillTargets(rows, canViewIp, retry, now).length > 0;
}

/**
 * Hält die Retry-Map klein: behält nur Einträge für aktuell noch ausstehende
 * (präsente + IP-lose) Spieler-Keys. Spieler, die inzwischen eine IP haben (→
 * Backfill erfolgreich) oder weg sind, fallen raus → kein Lockout, Map wächst
 * nicht unbegrenzt über Joins/Leaves hinweg.
 */
export function prunedIpRetry(retry: Map<string, number>, rows: ScorePlayer[]): Map<string, number> {
  const pending = new Set<string>();
  for (const p of rows) {
    const key = playerBackfillKey(p);
    if (key) pending.add(key);
  }
  const next = new Map<string, number>();
  for (const [key, at] of retry) {
    if (pending.has(key)) next.set(key, at);
  }
  return next;
}

export async function runConfirmedPlayerCommand(opts: {
  kind: "kick" | "ban";
  player: Pick<ScorePlayer, "id" | "name">;
  confirmFn: (message: string) => boolean;
  sendCommand: (cmd: string) => Promise<void>;
}): Promise<boolean> {
  const cmd = opts.kind === "kick" ? kickCommandForPlayer(opts.player.id) : banCommandForPlayer(opts.player.id);
  if (!cmd) return false;
  const label = opts.player.name || `Slot ${opts.player.id}`;
  const message = opts.kind === "kick"
    ? `${t("field.player")} "${label}" ${t("players.kickVerb")}?\n${t("players.slotId")}: ${opts.player.id}`
    : `${t("field.player")} "${label}" ${t("players.banEngine")}?\n${t("players.slotId")}: ${opts.player.id}\n${t("common.command")}: ${cmd}`;
  if (!opts.confirmFn(message)) return false;
  await opts.sendCommand(cmd);
  return true;
}

function CopyCell({ label, value, onCopy }: { label: "PID" | "IP"; value: string; onCopy: (label: "PID" | "IP", value: string) => void }) {
  const copyable = canCopyPlayerValue(value);
  return (
    <td class={`num mono faint compact ${copyable ? "copy-cell" : ""}`} data-label={label}>
      {copyable ? (
        <button class="copy-chip" type="button" title={`${label} ${t("players.copy")}`} onClick={() => onCopy(label, value)}>{value}</button>
      ) : "—"}
    </td>
  );
}

function TeamColumn({
  title,
  dot,
  players,
  canKick,
  canBan,
  canWarn,
  onCopy,
  onKick,
  onBan,
  onWarn,
}: {
  title: string;
  dot: string;
  players: ScorePlayer[];
  canKick: boolean;
  canBan: boolean;
  canWarn: boolean;
  onCopy: (label: "PID" | "IP", value: string) => void;
  onKick: (player: ScorePlayer) => void;
  onBan: (player: ScorePlayer) => void;
  onWarn: (player: ScorePlayer) => void;
}) {
  const totals = teamTotals(players);
  const showActions = canKick || canBan || canWarn;
  return (
    <div class={`panel team-panel ${dot}`}>
      <div class="panel-head">
        <h2><span class={`dot ${dot}`} style="margin-right:8px;display:inline-block" />{title}</h2>
        <div class="team-totals" aria-label={`${title} ${t("common.players")}`}>
          <span>{players.length} {t("common.players")}</span>
          <span>Σ {totals.score}</span>
          <span>K {totals.kills}</span>
          <span>D {totals.deaths}</span>
        </div>
      </div>
      <div class="panel-body" style="padding-top:var(--s4)">
        {players.length === 0 ? (
          <div class="empty">{t("players.empty")}</div>
        ) : (
          <table class="score">
            <thead>
              <tr>
                <th class="rank-head">{t("field.rank")}</th>
                <th>{t("field.player")}</th>
                <th class="num">PID</th>
                <th class="num">IP</th>
                <th class="num">Score</th>
                <th class="num">K</th>
                <th class="num">D</th>
                <th class="num">Ping</th>
                {showActions && <th class="num actions-head">{t("field.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.id}>
                  <RankCell rank={p.rank} />
                  <td><span class="pname">{p.name}{p.bot && <span class="caption">· Bot</span>}</span></td>
                  <CopyCell label="PID" value={p.pid} onCopy={onCopy} />
                  <CopyCell label="IP" value={p.ip} onCopy={onCopy} />
                  <td class="num pscore mono" data-label="Score">{p.score}</td>
                  <td class="num mono" data-label="K">{p.kills}</td>
                  <td class="num mono" data-label="D">{p.deaths}</td>
                  <td class="num mono faint" data-label="Ping">{p.ping || "—"}</td>
                  {showActions && (
                    <td class="num score-actions" data-label={t("field.actions")}>
                      {canWarn && <button class="mini-action" type="button" title={t("players.warnTitle")} onClick={() => onWarn(p)}>{t("players.warn")}</button>}
                      {canKick && <button class="mini-action" type="button" title={t("players.kickTitle")} onClick={() => onKick(p)}>Kick</button>}
                      {canBan && <button class="mini-action danger" type="button" title={t("players.banTitle")} onClick={() => onBan(p)}>Ban</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td class="rank-cell" data-label="Total">Σ</td>
                <td><span class="pname">TOTAL</span></td>
                <td class="num mono faint compact" data-label="PID">—</td>
                <td class="num mono faint compact" data-label="IP">—</td>
                <td class="num pscore mono" data-label="Score">{totals.score}</td>
                <td class="num mono" data-label="K">{totals.kills}</td>
                <td class="num mono" data-label="D">{totals.deaths}</td>
                <td class="num mono faint" data-label="Ping">—</td>
                {showActions && <td class="num score-actions" data-label={t("field.actions")}>—</td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/** Inline-Kalibrier-UX-Bündel für die TacticalMapPanel-Toolbar (Legacy-Port). */
export interface TacCalibUx {
  admin: boolean;
  mapSelected: boolean;
  fit: boolean;
  objects: boolean;
  cropMode: boolean;
  calMode: boolean;
  toggleFit: () => void;
  toggleObjects: () => void;
  toggleCrop: () => void;
  toggleCal: () => void;
  calPts: TacCalibCapture[];
  onCropSave: (crop: TacViewportRect | null) => void;
  onCalClick: (u: number, v: number) => void;
  openMapTab: () => void;
  editorOpen: boolean;
  openEditor: () => void;
  closeEditor: () => void;
}

/** Geteilte TAC-Toolbar (Fit/Objects/Crop/Calibrate [+Editor/Close]). */
function TacToolbar({ ctl, onClose }: { ctl: TacCalibUx; onClose?: () => void }) {
  return (
    <div class="tac-toolbar" role="toolbar" aria-label={t("players.tacControls")}>
      <button class={`btn sm tac-tool ${ctl.fit ? "active" : ""}`} type="button" onClick={ctl.toggleFit} title={t("players.tacFitTitle")}>Fit</button>
      <button class={`btn sm tac-tool ${ctl.objects ? "active" : ""}`} type="button" onClick={ctl.toggleObjects} title={t("players.tacObjectsTitle")}>{t("players.tacObjects")}</button>
      {ctl.admin && (
        <button class={`btn sm tac-tool ${ctl.cropMode ? "active" : ""}`} type="button" disabled={!ctl.mapSelected} onClick={ctl.toggleCrop} title={t("players.tacCropTitle")}>Crop</button>
      )}
      {ctl.admin && (
        <button class={`btn sm tac-tool ${ctl.calMode ? "active" : ""}`} type="button" disabled={!ctl.mapSelected} onClick={ctl.toggleCal} title={t("players.tacCalibrateTitle")}>{t("players.tacCalibrate")}</button>
      )}
      {ctl.admin && !onClose && (
        <button class="btn sm tac-tool" type="button" disabled={!ctl.mapSelected} onClick={ctl.openEditor} title={t("players.tacEditorTitle")}>Editor</button>
      )}
      {onClose && <button class="btn sm tac-tool tac-tool-close" type="button" onClick={onClose} title={t("players.tacCloseTitle")}>✕ {t("common.close")}</button>}
    </div>
  );
}

export interface TacSurfaceProps {
  mapName: string;
  modeName: string;
  total: number;
  maxPlayers: number;
  /** Punkte in VOLLBILD-uv (x,y ∈ 0..1) — die Surface projiziert sie selbst in ihren Viewport. */
  points: TacPoint[];
  calib: unknown;
  /** Roher Crop-Rect (Vollbild-uv) oder null. Die Surface rechnet daraus ihren Viewport. */
  crop: TacViewportRect | null;
  players: ScorePlayer[];
  ctl: TacCalibUx;
  /** Container-Aspect des Map-Containers (5/3 inline, 16/9 Editor). Default 5/3. */
  containerAspect?: number;
  /** Zusätzliche Klasse (z.B. `tac-map-editor` für das große Overlay). */
  extraClass?: string;
  /** Inline-Style (z.B. exakte px-Größe aus der gemessenen Body-Rect im Editor). */
  sizeStyle?: string;
}

/**
 * Die eigentliche Map-Fläche: Bild + Grid + Static-Objekte + Spieler + Crop-/
 * Calibrate-Interaktion. EIN Renderer für die kleine Live-Map UND das große
 * Editor-Overlay — keine doppelte Projektions-/Render-Logik. Transienter
 * Crop-Drag ist pro Instanz; Toggles/Save/calPts kommen aus `ctl` (geteilt).
 */
function TacMapSurface({ mapName, modeName, total, maxPlayers, points, calib, crop, players, ctl, containerAspect = 5 / 3, extraClass = "", sizeStyle }: TacSurfaceProps) {
  const asset = resolveTacMapAsset(mapName);
  const sectors = tacSectorLabels();
  const interacting = ctl.cropMode || ctl.calMode;
  // EINE Aspect-Quelle: die ECHTE DOM-Rect der Map-Fläche. So können Bild (CSS)
  // und Projektion (Viewport) nie auseinanderlaufen — egal welche CSS-Höhe/Breite
  // gilt. `containerAspect` ist nur der Start-Fallback bis zur ersten Messung.
  const mapRef = useRef<HTMLDivElement>(null);
  const measuredAspect = useSignal(containerAspect);
  useSignalEffect(() => {
    const el = mapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      // Quantisiert + peek-Vergleich: Sub-Pixel-Rauschen des ResizeObservers
      // (fraktionale Rects, Scrollbar-Erscheinen) darf keine Re-Render-Kaskade
      // bzw. keinen Viewport-Jitter auslösen.
      const next = quantizedAspect(el.clientWidth, el.clientHeight);
      if (next !== null && next !== measuredAspect.peek()) measuredAspect.value = next;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });
  const aspect = measuredAspect.value;
  // Viewport pro Surface aus rohem Crop + GEMESSENEM Container-Aspect. Crop > Cover.
  // Bild UND Punkte/Spieler teilen denselben Viewport (deckungsgleich).
  const cropVp = tacCropViewport(crop, 1, aspect);
  const viewport = cropVp ?? coverImageViewport(1, aspect);
  const cropActive = !!cropVp;
  // Punkte kommen in Vollbild-uv -> hier in den Viewport projizieren (out -> raus).
  const visiblePoints = visibleTacPoints(points)
    .map((p) => {
      const xy = mapImagePointToViewport({ x: p.x, y: p.y }, viewport);
      return xy ? { ...p, x: xy.x, y: xy.y } : null;
    })
    .filter((p): p is TacPoint => p !== null);
  const cropDrag = useSignal<TacViewportRect | null>(null);
  const eventToImage = (e: MouseEvent): { u: number; v: number } => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const f = pointFromClientRect(e.clientX, e.clientY, rect);
    return viewportToImagePoint(f.x, f.y, viewport);
  };
  const onMapDown = (e: MouseEvent) => {
    if (!ctl.cropMode) return;
    // Native Browser-Gesten unterbinden (Image-Drag/Text-Selektion), sonst
    // kapert der Browser die Crop-Geste und mousemove/mouseup kommen nie an.
    e.preventDefault();
    const p = eventToImage(e);
    cropDrag.value = { u0: p.u, v0: p.v, u1: p.u, v1: p.v };
  };
  const onMapMove = (e: MouseEvent) => {
    if (!ctl.cropMode || !cropDrag.value) return;
    const p = eventToImage(e);
    cropDrag.value = { ...cropDrag.value, u1: p.u, v1: p.v };
  };
  const onMapUp = () => {
    if (!ctl.cropMode || !cropDrag.value) return;
    const d = cropDrag.value;
    cropDrag.value = null;
    ctl.onCropSave(cropFromDrag({ u: d.u0, v: d.v0 }, { u: d.u1, v: d.v1 })); // null = Reset (bewusster Mini-Drag)
  };
  // Maus verlässt die Map mitten im Drag → ABBRECHEN, nicht speichern. Vorher
  // committete das (via onMouseLeave=onMapUp) ein Mini-Rect → cropFromDrag →
  // null → UNGEWOLLTER Crop-Reset. Genau das zerstörte auf Maps MIT vorhandenem
  // Crop (Legacy/Runtime) beim ersten Versuch den Crop — auf Suez (nie ein Crop
  // gespeichert) blieb der Fehl-Save unsichtbar, daher "geht nur auf Suez".
  const onMapLeave = () => {
    if (ctl.cropMode) cropDrag.value = null;
  };
  const onMapClick = (e: MouseEvent) => {
    if (!ctl.calMode) return;
    const p = eventToImage(e);
    ctl.onCalClick(p.u, p.v);
  };
  const toDisp = (u: number, v: number): { x: number; y: number } => ({
    x: (u - viewport.u0) / (viewport.u1 - viewport.u0),
    y: (v - viewport.v0) / (viewport.v1 - viewport.v0),
  });
  const telemetry = playerTacTelemetry(players);
  const mappedPlayers = telemetry
    .map((p) => {
      const uv = mapWorldToTacImagePoint(calib, p.world);
      return { player: p, point: uv ? mapImagePointToViewport(uv, viewport) : null };
    })
    .filter((p): p is { player: PlayerTacTelemetry; point: { x: number; y: number } } => p.point !== null);
  const telemetryState: TacTelemetryState = mappedPlayers.length > 0
    ? "live"
    : telemetry.length > 0
      ? "needs-calibration"
      : players.length > 0
        ? "no-position"
        : "offline";
  return (
    <div
      ref={mapRef}
      style={sizeStyle}
      class={`tac-map ${asset.known ? "has-map" : "no-map"} ${tacMapStateClass(telemetryState)} ${interacting ? "tac-interacting" : ""} ${ctl.cropMode ? "tac-cropmode" : ""} ${ctl.calMode ? "tac-calmode" : ""} ${extraClass}`}
      onMouseDown={onMapDown}
      onMouseMove={onMapMove}
      onMouseUp={onMapUp}
      onMouseLeave={onMapLeave}
      onDragStart={(e: DragEvent) => e.preventDefault()}
      onClick={onMapClick}
    >
      {asset.src && (cropActive
        ? <div class="tac-map-img tac-map-img-crop" style={tacCropImageStyle(asset.src, viewport)} role="img" aria-label={`${asset.label} Minimap`} />
        : <img class="tac-map-img" src={asset.src} alt={`${asset.label} Minimap`} draggable={false} />)}
      <div class="tac-grid" aria-hidden="true" />
      <div class="tac-sector-grid" aria-hidden="true">
        {sectors.map((s) => <span key={s}>{s}</span>)}
      </div>
      <div class="tac-bearing north" aria-hidden="true">N</div>
      <div class="tac-bearing east" aria-hidden="true">E</div>
      <div class="tac-bearing south" aria-hidden="true">S</div>
      <div class="tac-bearing west" aria-hidden="true">W</div>
      <div class="tac-sector pac" aria-hidden="true">PAC</div>
      <div class="tac-sector eu" aria-hidden="true">EU</div>
      <div class="tac-saved-points" aria-label="Gespeicherte TAC-Punkte">
        {visiblePoints.map((p) => (
          <span
            key={p.id}
            class={`tac-point ${p.team ?? "neutral"} ${p.kind} ${p.icon ? "has-icon" : ""}`}
            style={`left:${(p.x * 100).toFixed(3)}%;top:${(p.y * 100).toFixed(3)}%`}
            title={`${p.label} · ${p.kind}${p.flagName ? ` · ${p.flagName}` : ""}`}
          >
            <i>
              {p.icon
                ? <img src={p.icon} alt="" aria-hidden="true" style={p.rot ? `transform:rotate(${p.rot}deg)` : undefined} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                : p.kind.slice(0, 1).toUpperCase()}
            </i>
            <b>{p.label}</b>
          </span>
        ))}
      </div>
      <div class="tac-live-players" aria-label="Live-Spielerpositionen">
        {mappedPlayers.map(({ player, point }) => {
          const team = player.team === 1 ? "pac" : player.team === 2 ? "eu" : "neutral";
          const meta = tacPlayerMeta(player);
          const kitSrc = kitIconPath(player.kit);
          const vehicleSrc = vehicleIconPath(player.vehicle, player.team);
          return (
            <span
              key={`${player.id}-${player.pid || player.name}`}
              class={`tac-live-player ${team} ${player.commander ? "commander" : ""} ${player.squadLeader ? "squad-leader" : ""}`}
              style={`left:${(point.x * 100).toFixed(3)}%;top:${(point.y * 100).toFixed(3)}%`}
              title={`${player.name}${meta ? ` · ${meta}` : ""}`}
            >
              <i>{player.commander ? "CMD" : player.squadLeader ? "SL" : ""}</i>
              <b>
                {vehicleSrc && <img src={vehicleSrc} alt="" aria-hidden="true" />}
                {kitSrc && <img src={kitSrc} alt="" aria-hidden="true" />}
                <span>{player.name}</span>
              </b>
              {meta && <em>{meta}</em>}
            </span>
          );
        })}
      </div>
      {ctl.cropMode && cropDrag.value && (() => {
        const d = cropDrag.value;
        const a = toDisp(Math.min(d.u0, d.u1), Math.min(d.v0, d.v1));
        const b = toDisp(Math.max(d.u0, d.u1), Math.max(d.v0, d.v1));
        return (
          <div
            class="tac-crop-rect"
            aria-hidden="true"
            style={`left:${(a.x * 100).toFixed(2)}%;top:${(a.y * 100).toFixed(2)}%;width:${((b.x - a.x) * 100).toFixed(2)}%;height:${((b.y - a.y) * 100).toFixed(2)}%`}
          />
        );
      })()}
      {ctl.calMode && (
        <div class="tac-cal-points" aria-hidden="true">
          {ctl.calPts.map((c, i) => {
            const dp = toDisp(c.p[0], c.p[1]);
            return <span key={i} class="tac-cal-point" style={`left:${(dp.x * 100).toFixed(2)}%;top:${(dp.y * 100).toFixed(2)}%`}>{i + 1}</span>;
          })}
        </div>
      )}
      {showTacCenterInfo(telemetryState) && !interacting && (
        <div class="tac-center">
          <span>{asset.label}</span>
          <small>{modeLabel(modeName)} · {formatTacPlayerCount(total, maxPlayers)} {t("common.players").toUpperCase()}</small>
        </div>
      )}
      {interacting ? (
        <div class="tac-status editing">
          {ctl.cropMode
            ? t("players.tacCropStatus")
            : `${t("players.tacCalStatus")} ${Math.min(ctl.calPts.length + 1, 3)}/3`}
        </div>
      ) : (
        <div class={`tac-status ${telemetryState}`}>{tacTelemetryStatusFor(telemetryState)}</div>
      )}
    </div>
  );
}

function TacticalMapPanel({
  serverName,
  mapName,
  modeName,
  pacCount,
  euCount,
  total,
  maxPlayers,
  resource,
  resourceKnown,
  points,
  calib,
  crop,
  players,
  ctl,
}: {
  serverName: string;
  mapName: string;
  modeName: string;
  pacCount: number;
  euCount: number;
  total: number;
  maxPlayers: number;
  resource: BattleResource;
  resourceKnown: boolean;
  points: TacPoint[];
  calib: unknown;
  crop: TacViewportRect | null;
  players: ScorePlayer[];
  ctl: TacCalibUx;
}) {
  const asset = resolveTacMapAsset(mapName);
  return (
    <Panel title="TACTICAL MAP">
      <div class="tac-panel">
        <div class="tac-command-strip">
          <div>
            <span class="tac-kicker">TACTICAL INTEL</span>
            <strong>{asset.label}</strong>
          </div>
          <TacToolbar ctl={ctl} />
        </div>
        <TacMapSurface
          mapName={mapName} modeName={modeName} total={total} maxPlayers={maxPlayers}
          points={points} calib={calib} crop={crop} players={players} ctl={ctl}
        />
        <div class="tac-meta">
          <span><b>Server</b>{serverName}</span>
          <span><b>{t("common.map")}</b>{asset.label}</span>
          <span><b>{t("common.mode")}</b>{modeLabel(modeName)}</span>
          <span><b>{t("common.players")}</b>{formatTacPlayerCount(total, maxPlayers)}</span>
          <span class="pac-chip"><b>PAC</b>{pacCount}</span>
          <span class="eu-chip"><b>EU</b>{euCount}</span>
        </div>
        <div class={`tac-resource ${resource.titan ? "titan" : ""}`}>
          <span class="tac-resource-label">{resource.label}</span>
          <span class="pac-chip"><b>PAC</b>{resourceKnown ? resource.pac : "—"}</span>
          <span class="eu-chip"><b>EU</b>{resourceKnown ? resource.eu : "—"}</span>
        </div>
      </div>
    </Panel>
  );
}

/**
 * Großer Fullscreen-Editor (Fokus-Modus) — gleiche `TacMapSurface`, nur groß.
 * Wird per Portal direkt an `document.body` gehängt, damit er NICHT im
 * `.view-enter`/transformierten View-Container steckt (sonst ist `position:fixed`
 * nicht viewport-global und die Map würde vom Layout begrenzt).
 */
function TacCalibrationOverlay({ surface }: { surface: TacSurfaceProps }) {
  const { ctl } = surface;
  // Robust: die ECHTE Body-Rect messen und daraus die größtmögliche 16:9-Map
  // berechnen (w = min(bodyW, bodyH·16/9)). Als inline-px an die Map -> keine
  // geratenen vh-Formeln, keine alte max-width-Deckelung. Fallback = CSS.
  const bodyRef = useRef<HTMLDivElement>(null);
  const mapSize = useSignal<{ w: number; h: number } | null>(null);
  useSignalEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ASPECT = 16 / 9;
    const measure = () => {
      const bw = el.clientWidth, bh = el.clientHeight;
      if (bw > 0 && bh > 0) {
        const w = Math.min(bw, bh * ASPECT);
        const next = { w: Math.floor(w), h: Math.floor(w / ASPECT) };
        // Nur bei ECHTER Größenänderung schreiben — ein neues, wertgleiches
        // Objekt würde sonst bei jedem ResizeObserver-Tick re-rendern (Jitter).
        const prev = mapSize.peek();
        if (!prev || prev.w !== next.w || prev.h !== next.h) mapSize.value = next;
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });
  const sizeStyle = mapSize.value ? `width:${mapSize.value.w}px;height:${mapSize.value.h}px` : undefined;
  const overlay = (
    <div class="tac-editor-overlay" role="dialog" aria-modal="true" aria-label="TAC Map Kalibrierung">
      <div class="tac-editor-backdrop" onClick={ctl.closeEditor} />
      <div class="tac-editor-card">
        <div class="tac-editor-head">
          <strong class="tac-editor-title">
            TAC MAP · {resolveTacMapAsset(surface.mapName).label}
            {ctl.fit && <span class="tac-fit-flag" title={t("players.tacFitFlagTitle")}>FIT {t("status.on")}</span>}
          </strong>
          <TacToolbar ctl={ctl} onClose={ctl.closeEditor} />
        </div>
        <div class="tac-editor-body" ref={bodyRef}>
          <TacMapSurface {...surface} containerAspect={16 / 9} extraClass="tac-map-editor" sizeStyle={sizeStyle} />
        </div>
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(overlay, document.body) : overlay;
}

export function ticketsFromStatus(status: Record<string, unknown>): { t1: number; t2: number } {
  return {
    t1: Number(status.ticketsPac ?? status.t1) || 0,
    t2: Number(status.ticketsEu ?? status.t2) || 0,
  };
}

export function formatTacPlayerCount(total: number, maxPlayers: number): string {
  const current = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  return `${current}${maxPlayers ? `/${maxPlayers}` : ""}`;
}

/** Toggle-Persistenz wie Legacy: tacFit Default aus ('1'=an), tacObjects Default an ('0'=aus). */
function readTacToggle(key: string, dflt: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return dflt;
    return dflt ? v !== "0" : v === "1";
  } catch {
    return dflt;
  }
}
function writeTacToggle(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? "1" : "0");
  } catch {
    /* localStorage kann scheitern */
  }
}

export function Players() {
  const admin = isAdmin();
  // Web-Moderation (#10): einzeln permission-gegatet. Server bleibt die Wahrheit.
  const canKick = hasPermission("action.kick");
  const canBan = hasPermission("action.pid_ban");
  const canWarn = hasPermission("action.warn");
  const canAutobalance = hasPermission("action.autobalance");
  // IP-Spalte ist permission-gegatet; nur mit dieser Permission wird die IP
  // überhaupt nachgeladen (Server liefert sie sonst geleert) → kein Leak.
  const canViewIp = hasPermission("action.view_ip");
  const rows = useSignal<ScorePlayer[]>([]);
  const mapName = useSignal("");
  const modeName = useSignal("");
  const maxp = useSignal(0);
  const tickets = useSignal<{ t1: number; t2: number } | null>(null);
  const tacMaps = useSignal<Record<string, { points: TacPoint[]; calib?: unknown; crop?: unknown }>>({});
  const staticObjects = useSignal<StaticObjectData>({});
  const loading = useSignal(false);
  const notice = useSignal("");
  const edit = useSignal(false);
  // Inline-TAC-Kalibrier-UX (Legacy-Toolbar). Fit/Objects = View-Präferenz
  // (localStorage), Crop/Calibrate = Bearbeitungs-Modi (Admin).
  const tacFit = useSignal(readTacToggle("tacFit", false));
  const tacObjects = useSignal(readTacToggle("tacObjects", true));
  const tacCropMode = useSignal(false);
  const tacCalMode = useSignal(false);
  const tacCalPts = useSignal<TacCalibCapture[]>([]);
  const tacEditorOpen = useSignal(false);

  // ESC schließt den Kalibrier-Editor (und beendet die Modi).
  useSignalEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && tacEditorOpen.value) {
        tacEditorOpen.value = false;
        tacCropMode.value = false;
        tacCalMode.value = false;
        tacCalPts.value = [];
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  async function load() {
    const sv = activeServer.value;
    if (!sv) return;
    loading.value = true;
    notice.value = "";
    try {
      const [inf, st, pl] = await Promise.allSettled([
        api.call<ApiResponse & { map: string; mode: string; maxplayers: string }>("server_info", { server: sv }),
        api.call<ApiResponse & { status: Record<string, unknown> | null }>("server_status", { server: sv }),
        api.call<ApiResponse & { raw: string }>("players_list", { server: sv }),
      ]);
      if (inf.status === "fulfilled" && inf.value.ok) {
        mapName.value = inf.value.map || mapName.value;
        modeName.value = inf.value.mode || modeName.value;
        maxp.value = Number(inf.value.maxplayers) || 0;
      }
      if (st.status === "fulfilled" && st.value.ok && st.value.status) {
        const s = st.value.status;
        tickets.value = ticketsFromStatus(s);
        if (!mapName.value) mapName.value = String(s.map ?? "");
        if (!modeName.value) modeName.value = String(s.mode ?? "");
      }
      if (pl.status === "fulfilled" && pl.value.ok) rows.value = parseBf2ccPl(pl.value.raw);
      if (rows.value.length === 0 && live.players.value.length === 0) {
        notice.value = t("players.noLiveData");
      }
    } finally {
      loading.value = false;
    }
  }

  // Retry-Zeitpunkt pro Spieler-Key (kein Re-render → useRef) + expliziter
  // In-Flight-Guard. `usePolling`/`createPoller` verhindert Overlap bereits,
  // der Guard macht "kein paralleler Backfill" zusätzlich lokal explizit.
  const ipRetry = useRef<Map<string, number>>(new Map());
  const ipBackfillBusy = useRef(false);

  // Permission-gated IP-Nachzug: holt die Spielerliste über den server-seitig
  // gegateten REST-Pfad (`players_list`) und merged NUR die fehlende IP nach.
  // Läuft ausschließlich, wenn es fällige Targets gibt (User darf IP sehen UND ein
  // echter Nicht-Bot-Spieler — auch Commander — ohne IP, dessen Retry-Cooldown
  // abgelaufen ist). Der WS bleibt IP-frei.
  async function backfillIps() {
    const sv = activeServer.value;
    if (!sv || ipBackfillBusy.current) return;
    const now = Date.now();
    const targets = ipBackfillTargets(rows.value, canViewIp, ipRetry.current, now);
    if (targets.length === 0) return;
    // Nächsten Retry-Zeitpunkt je Target setzen, BEVOR der Request läuft → kein
    // aggressives Dauerpolling; nach Ablauf wird (falls IP weiter fehlt) erneut
    // versucht (kein dauerhafter Lockout).
    for (const key of targets) ipRetry.current.set(key, now + IP_BACKFILL_RETRY_MS);
    ipBackfillBusy.current = true;
    try {
      const r = await api.call<ApiResponse & { raw: string }>("players_list", { server: sv });
      // peek(): Read-modify-write immer untracked — konsistent zum Live-Merge-
      // Effekt (dort verhindert es den Selbst-Retrigger-Zyklus).
      if (r.ok) rows.value = mergeScorePlayers(rows.peek(), parseBf2ccPl(r.raw));
    } finally {
      // Map klein halten: nur noch ausstehende Keys behalten. Wer jetzt eine IP
      // hat (Backfill erfolgreich) oder weg ist, fällt raus → Retry-State gelöscht.
      ipRetry.current = prunedIpRetry(ipRetry.current, rows.value);
      ipBackfillBusy.current = false;
    }
  }

  async function loadTacPoints() {
    const r = await api.call<ApiResponse & { maps: Array<{ image: string; points?: TacPoint[]; calib?: unknown; crop?: unknown }> }>("tac_maps");
    if (!r.ok) return;
    const next: Record<string, { points: TacPoint[]; calib?: unknown; crop?: unknown }> = {};
    for (const m of r.maps ?? []) next[m.image] = { points: m.points ?? [], calib: m.calib, crop: m.crop };
    tacMaps.value = next;
  }

  // Legacy-Static-Objects (Flaggen/Bases/Silos) einmal best-effort laden.
  async function loadStaticObjects() {
    try {
      const res = await fetch("/assets/static_objects.json");
      if (!res.ok) return;
      staticObjects.value = parseStaticObjects(await res.json());
    } catch {
      // Fehlende/defekte Datei -> keine static Punkte, kein Crash.
    }
  }

  // REST neu laden bei Serverwechsel.
  useSignalEffect(() => {
    if (activeServer.value) {
      void load();
      void loadTacPoints();
    }
  });

  // Static-Objects sind global -> nur einmal laden (liest keine Signals).
  useSignalEffect(() => { void loadStaticObjects(); });

  // Live-Updates aus dem WS (überschreiben REST, sobald vorhanden).
  // WICHTIG: rows via `peek()` lesen — `rows.value` würde den Effekt auf rows
  // subscriben; da mergeScorePlayers IMMER ein neues Array liefert, triggert
  // der eigene Write den Effekt dann endlos erneut ("Cycle detected" nach 100
  // Iterationen pro WS-Push → Render-Sturm, TAC-Map-Flackern, CPU-Spike).
  useSignalEffect(() => {
    const lp = live.players.value;
    if (lp.length) rows.value = mergeScorePlayers(rows.peek(), fromLivePlayers(lp));
    const ls = live.status.value;
    if (ls) {
      tickets.value = ticketsFromStatus(ls);
      if (ls.map) mapName.value = String(ls.map);
      if (ls.mode) modeName.value = String(ls.mode);
    }
  });

  // Neue Joiner kommen nur per (IP-freiem) WS rein → ihre IP würde fehlen.
  // Throttled über den gated REST-Pfad nachziehen; entkoppelt vom Signal-Graph
  // (Timer), läuft nur bei tatsächlicher Lücke + Permission (kein Dauer-Poll).
  usePolling(() => void backfillIps(), IP_BACKFILL_POLL_MS);

  const byScore = (a: ScorePlayer, b: ScorePlayer) => b.score - a.score;
  const pac = rows.value.filter((p) => p.team === 1).sort(byScore);
  const eu = rows.value.filter((p) => p.team === 2).sort(byScore);
  const total = rows.value.length;
  const serverName = servers.value.find((s) => s.id === activeServer.value)?.name ?? "—";

  const resource = formatBattleResource(modeName.value, tickets.value?.t1 ?? 0, tickets.value?.t2 ?? 0);
  const tacAsset = resolveTacMapAsset(mapName.value);
  const currentTacKey = tacPointImageKey(tacAsset.src);
  const currentTacMap = tacMaps.value[currentTacKey];
  const currentTacPoints = currentTacMap?.points ?? [];
  const currentTacCalib = resolveTacCalibration(currentTacMap?.calib, currentTacKey);
  const tacMapSelected = tacAsset.known && currentTacKey !== "";
  const tacInteracting = tacCropMode.value || tacCalMode.value;

  // Roh-Weltkoordinaten der Static-Objects (für Fit-Bbox; viewport-unabhängig).
  const curatedTitan = modeName.value === "gpm_ti" ? curatedTitanMap(mapName.value) : null;
  const staticVariantsAll = staticObjectsForMap(staticObjects.value, mapName.value);
  const staticVariantKey = pickStaticObjectVariant(staticVariantsAll, modeName.value, maxp.value);
  const staticWorld: { x: number; z: number }[] = curatedTitan
    ? [...curatedTitan.titans, ...curatedTitan.silos].map((o) => ({ x: o.x, z: o.z }))
    : ((staticVariantKey && staticVariantsAll ? staticVariantsAll[staticVariantKey]! : []).map((o) => ({ x: Number(o.x) || 0, z: Number(o.z) || 0 })));

  // Fit-Crop = bbox der Objektpunkte (Vollbild-uv) + Margin. Nur wenn Fit an und
  // kein Bearbeitungs-Modus aktiv. Wird NICHT gespeichert (reine View-Präferenz).
  const fitCrop = (!tacInteracting && tacFit.value)
    ? fitViewportFromImagePoints(
        staticWorld
          .map((w) => mapWorldToTacImagePoint(currentTacCalib, w))
          .filter((p): p is { x: number; y: number } => p !== null)
          .map((p) => ({ u: p.x, v: p.y })),
      )
    : null;

  // Roher Crop (Vollbild-uv): im Crop/Calibrate-Modus volle Karte (gegen Vollbild
  // kalibrieren), sonst manueller Crop (Runtime>Legacy) VOR Fit. Der Viewport wird
  // PRO Surface (mit deren Container-Aspect: 5/3 inline, 16/9 Editor) berechnet.
  const manualCrop = resolveTacCrop(currentTacMap?.crop, currentTacKey);
  const effectiveCrop = (tacInteracting ? null : (manualCrop ?? fitCrop)) as TacViewportRect | null;

  // Static-Objekte in VOLLBILD-uv projizieren (aspect-unabhängig); die Surface
  // projiziert sie dann in ihren Viewport. EIN Pfad für Flaggen, Silos, Titane.
  const projectUv = (calib: unknown, world: { x: number; z: number }) => mapWorldToTacImagePoint(calib, world);
  let staticTacPoints: TacPoint[];
  if (curatedTitan) {
    staticTacPoints = curatedToTacPoints(curatedTitan, currentTacCalib, projectUv);
  } else {
    staticTacPoints = staticVariantKey && staticVariantsAll
      ? staticObjectsToTacPoints(staticVariantsAll[staticVariantKey]!, currentTacCalib, staticVariantKey, projectUv)
      : [];
  }
  // Objects-Toggle blendet NUR Static-Objects aus (Spielerpunkte bleiben immer).
  const visibleStatic = tacObjects.value ? staticTacPoints : [];
  // Manuelle + Static-Punkte in Vollbild-uv mergen; die Surface projiziert.
  const tacPoints = mergeTacPoints(currentTacPoints, visibleStatic);

  // --- Inline-Kalibrier-Toolbar/Modi (Legacy-UX) ---
  const toggleTacFit = () => { tacFit.value = !tacFit.value; writeTacToggle("tacFit", tacFit.value); };
  const toggleTacObjects = () => { tacObjects.value = !tacObjects.value; writeTacToggle("tacObjects", tacObjects.value); };
  const toggleTacCrop = () => {
    if (!tacMapSelected) return toast(t("maps.toastPickMap"), "err");
    tacCropMode.value = !tacCropMode.value;
    if (tacCropMode.value) { tacCalMode.value = false; tacEditorOpen.value = true; } // Crop öffnet den großen Editor
    toast(tacCropMode.value ? t("players.tacCropHint") : t("players.tacCropEnd"), "ok");
  };
  const toggleTacCal = () => {
    if (!tacMapSelected) return toast(t("maps.toastPickMap"), "err");
    tacCalMode.value = !tacCalMode.value;
    tacCalPts.value = [];
    if (tacCalMode.value) { tacCropMode.value = false; tacEditorOpen.value = true; } // Calibrate öffnet den großen Editor
    toast(tacCalMode.value ? t("players.tacCalHint") : t("players.tacCalCancel"), tacCalMode.value ? "ok" : "err");
  };
  const openTacEditor = () => {
    if (!tacMapSelected) return toast(t("maps.toastPickMap"), "err");
    tacEditorOpen.value = true;
  };
  const closeTacEditor = () => {
    tacEditorOpen.value = false;
    tacCropMode.value = false;
    tacCalMode.value = false;
    tacCalPts.value = [];
  };
  async function saveTacCrop(crop: TacViewportRect | null) {
    if (!currentTacKey) return;
    try {
      const r = await api.call<ApiResponse>("tac_calib_save", { image: currentTacKey, crop });
      if (r.ok) { toast(crop ? t("players.tacCropSaved") : t("players.tacCropReset"), "ok"); await loadTacPoints(); }
      else toast(String(r.error ?? t("players.tacSaveFailed")), "err");
    } catch { toast(t("players.tacSaveFailed"), "err"); }
  }
  function captureCalibWorld(): [number, number] | null {
    const tele = playerTacTelemetry(rows.value).filter((tp) => !(tp.world.x === 0 && tp.world.z === 0));
    if (tele.length === 1) { const tp = tele[0]!; toast(`${t("players.tacPosFrom")} ${tp.name} ${t("players.tacPosCaptured")}`, "ok"); return [tp.world.x, tp.world.z]; }
    const num = tacCalPts.value.length + 1;
    if (tele.length > 1) {
      const list = tele.map((tp, i) => `${i + 1}) ${tp.name} (${Math.round(tp.world.x)}, ${Math.round(tp.world.z)})`).join("\n");
      const sel = prompt(`${t("players.tacPoint")} ${num}/3 — ${t("players.tacWhoOnSpot")}\n${list}\n\n${t("players.tacPickNumber")}`);
      if (sel == null) return null;
      const idx = parseInt(sel, 10);
      if (idx >= 1 && idx <= tele.length) { const tp = tele[idx - 1]!; return [tp.world.x, tp.world.z]; }
      const n = sel.split(/[,\s/]+/).map(Number).filter((x) => !Number.isNaN(x));
      return n.length >= 2 ? [n[0]!, n.length >= 3 ? n[2]! : n[1]!] : null;
    }
    const ans = prompt(`${t("players.tacPoint")} ${num}/3 — ${t("players.tacNoLiveCoords")}\n${t("players.tacManualXZ")}`);
    if (ans == null) return null;
    const n = ans.split(/[,\s/]+/).map(Number).filter((x) => !Number.isNaN(x));
    return n.length >= 2 ? [n[0]!, n.length >= 3 ? n[2]! : n[1]!] : null;
  }
  async function onTacCalClick(u: number, v: number) {
    const w = captureCalibWorld();
    if (!w) { toast(t("players.tacNoWorld"), "err"); return; }
    const pts = [...tacCalPts.value, { p: [u, v] as [number, number], w }];
    tacCalPts.value = pts;
    if (pts.length >= 3) {
      tacCalMode.value = false;
      try {
        const r = await api.call<ApiResponse>("tac_calib_save", { image: currentTacKey, calib: pts });
        if (r.ok) { toast(t("players.tacCalSaved"), "ok"); await loadTacPoints(); }
        else toast(String(r.error ?? t("players.tacSaveFailed")), "err");
      } catch { toast(t("players.tacSaveFailed"), "err"); }
      tacCalPts.value = [];
    }
  }
  const tacCtl: TacCalibUx = {
    admin, mapSelected: tacMapSelected,
    fit: tacFit.value, objects: tacObjects.value, cropMode: tacCropMode.value, calMode: tacCalMode.value,
    toggleFit: toggleTacFit, toggleObjects: toggleTacObjects, toggleCrop: toggleTacCrop, toggleCal: toggleTacCal,
    calPts: tacCalPts.value, onCropSave: (c) => void saveTacCrop(c), onCalClick: (u, v) => void onTacCalClick(u, v),
    openMapTab: () => { location.hash = `#/${tacCalibrationTarget()}`; },
    editorOpen: tacEditorOpen.value, openEditor: openTacEditor, closeEditor: closeTacEditor,
  };
  const tacSurface: TacSurfaceProps = {
    mapName: mapName.value, modeName: modeName.value, total, maxPlayers: maxp.value,
    points: tacPoints, calib: currentTacCalib, crop: effectiveCrop,
    players: rows.value, ctl: tacCtl,
  };

  async function modCall(actionName: string, body: Record<string, unknown>): Promise<boolean> {
    const sv = activeServer.value;
    if (!sv) { toast(t("view.noServerSelected"), "err"); return false; }
    const r = await api.call<ApiResponse>(actionName, { server: sv, ...body });
    return r.ok;
  }

  async function runKick(player: ScorePlayer) {
    const label = player.name || `Slot ${player.id}`;
    if (!confirm(`${t("field.player")} "${label}" ${t("players.kickVerb")}?\n${t("players.slotId")}: ${player.id}`)) return;
    const ok = await modCall("mod_kick", { id: player.id, pid: player.pid, name: player.name });
    toast(ok ? t("players.kickSent") : t("players.kickFailed"), ok ? "ok" : "err");
    if (ok) setTimeout(() => void load(), 600);
  }

  async function runBan(player: ScorePlayer) {
    const label = player.name || `Slot ${player.id}`;
    const pid = String(player.pid ?? "").replace(/\D/g, "");
    if (!pid) { toast(t("players.banNoPid"), "err"); return; }
    if (!confirm(`${t("field.player")} "${label}" ${t("players.banPidConfirm")}\nPID: ${pid}`)) return;
    const reason = prompt(t("players.banReasonPrompt"), "") ?? "";
    const ok = await modCall("mod_pid_ban", { pid, id: player.id, name: player.name, reason });
    toast(ok ? t("players.banSent") : t("players.banFailed"), ok ? "ok" : "err");
    if (ok) setTimeout(() => void load(), 600);
  }

  async function runWarn(player: ScorePlayer) {
    const message = prompt(`${t("players.warnPrompt")} ${player.name || `Slot ${player.id}`}:`, "");
    if (message == null || message.trim() === "") return;
    const ok = await modCall("mod_warn", { name: player.name, pid: player.pid, message: message.trim() });
    toast(ok ? t("players.warnSent") : t("players.warnFailed"), ok ? "ok" : "err");
  }

  async function runAutobalance(on: boolean) {
    const ok = await modCall("mod_autobalance", { on });
    toast(ok ? (on ? t("players.abOn") : t("players.abOff")) : t("common.error"), ok ? "ok" : "err");
  }

  async function copyValue(label: "PID" | "IP", value: string) {
    if (await copyTextToClipboard(value)) {
      toast(`${label} ${t("players.copied")}`, "ok");
    } else {
      toast(`${label} ${t("players.copyFailed")}`, "err");
    }
  }

  // Default-Layout = aktuelles Aussehen: 4 Stats (¼), 2 Team-Spalten (½), TAC (voll).
  const items: PanelItem[] = [
    { id: "stat-players", title: t("common.players"), defaultSpan: 3, render: () => <Stat label={t("common.players")} icon="▣" value={total || "—"} sub={maxp.value ? `${t("players.of")} ${maxp.value} max` : undefined} /> },
    { id: "stat-map", title: t("common.map"), defaultSpan: 3, render: () => <Stat label={t("common.map")} icon="◈" value={<span class="value text">{mapName.value ? formatMapName(mapName.value) : "—"}</span>} /> },
    { id: "stat-mode", title: t("common.mode"), defaultSpan: 3, render: () => <Stat label={t("common.mode")} icon="◆" value={<span class="value text">{modeLabel(modeName.value)}</span>} /> },
    { id: "stat-tickets", title: "Tickets", defaultSpan: 3, render: () => (
      <Stat label={resource.label} icon="◷" sub={resource.sub} value={
        <span class={`row mono battle-resource ${resource.titan ? "titan-resource" : ""}`} style="gap:var(--s2);flex-wrap:wrap">
          <span class="dot pac" /><span class={resource.titan ? "pac-text" : ""}>{tickets.value ? resource.pac : "—"}</span>
          <span class="faint" style="margin:0 4px">{resource.titan ? "/" : ":"}</span>
          <span class="dot eu" /><span class={resource.titan ? "eu-text" : ""}>{tickets.value ? resource.eu : "—"}</span>
        </span>
      } />
    ) },
    { id: "team-pac", title: "Team PAC", defaultSpan: 6, render: () => <TeamColumn title="Team PAC" dot="pac" players={pac} canKick={canKick} canBan={canBan} canWarn={canWarn} onCopy={(label, value) => void copyValue(label, value)} onKick={(p) => void runKick(p)} onBan={(p) => void runBan(p)} onWarn={(p) => void runWarn(p)} /> },
    { id: "team-eu", title: "Team EU", defaultSpan: 6, render: () => <TeamColumn title="Team EU" dot="eu" players={eu} canKick={canKick} canBan={canBan} canWarn={canWarn} onCopy={(label, value) => void copyValue(label, value)} onKick={(p) => void runKick(p)} onBan={(p) => void runBan(p)} onWarn={(p) => void runWarn(p)} /> },
    { id: "tac", title: "Tactical Map", defaultSpan: 12, render: () => <TacticalMapPanel serverName={serverName} mapName={mapName.value} modeName={modeName.value} pacCount={pac.length} euCount={eu.length} total={total} maxPlayers={maxp.value} resource={resource} resourceKnown={!!tickets.value} points={tacPoints} calib={currentTacCalib} crop={effectiveCrop} players={rows.value} ctl={tacCtl} /> },
  ];

  return (
    <div class="view-enter">
      <div class="players-actions">
        <div class="row">
          <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
          <button class="btn sm primary" onClick={() => void load()} disabled={loading.value}>↻ {t("common.refresh")}</button>
          {canAutobalance && (
            <span class="row" style="gap:6px;margin-left:auto;align-items:center">
              <span class="caption">{t("players.autobalance")}</span>
              <button class="btn sm" type="button" onClick={() => void runAutobalance(true)}>{t("status.on")}</button>
              <button class="btn sm ghost" type="button" onClick={() => void runAutobalance(false)}>{t("status.off")}</button>
            </span>
          )}
        </div>
      </div>

      {total === 0 && <div class="caption" style="margin-bottom:var(--s3)">{notice.value}</div>}
      <PanelGrid view="players" items={items} edit={edit} />
      {tacEditorOpen.value && <TacCalibrationOverlay surface={tacSurface} />}
      {/* Frei verschiebbares Killfeed-Overlay für DIESEN Server (nur bei aktivem
          Server + view.agent_events; sonst rendert die Komponente nichts). */}
      <ServerKillfeedPanel serverId={activeServer.value} />
    </div>
  );
}
